import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Workflow, PenLine } from 'lucide-react';
import axios from 'axios';

const SLIDES = [
  {
    tag: 'BA Workbench',
    icon: 'workflow',
    headline: ['Structure your', 'analysis.', 'Ship with clarity.'],
    body: 'A professional workbench for Business Analysts — from requirements capture to delivery tracking, with AI where it matters most.',
    features: [
      { title: 'Requirements & compliance', desc: 'Structure BRDs, run IRAC legal checks, and manage rule engines' },
      { title: 'AI-assisted analysis', desc: 'Impact mapping, stakeholder analysis, and job intelligence agents' },
      { title: 'Full delivery tracking', desc: 'Kanban boards, timelines, team hours, and handover documents' },
    ],
    accent: '#7eaaff',
  },
];

export default function AuthPage() {
  const [slide, setSlide] = useState(0);
  const [displaySlide, setDisplaySlide] = useState(0);
  const [wipePhase, setWipePhase] = useState('idle'); // 'idle' | 'in' | 'out'
  const wipingRef = useRef(false);
  const nextSlideRef = useRef(null);

  const [isLogin, setIsLogin] = useState(true);
  const [forgotMode, setForgotMode] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const triggerSlide = useCallback((next) => {
    if (wipingRef.current || next === slide) return;
    wipingRef.current = true;
    nextSlideRef.current = next;
    setWipePhase('in');
  }, [slide]);

  useEffect(() => {
    if (wipePhase === 'in') {
      const t = setTimeout(() => {
        setDisplaySlide(nextSlideRef.current);
        setSlide(nextSlideRef.current);
        setWipePhase('out');
      }, 260);
      return () => clearTimeout(t);
    }
    if (wipePhase === 'out') {
      const t = setTimeout(() => {
        setWipePhase('idle');
        wipingRef.current = false;
      }, 260);
      return () => clearTimeout(t);
    }
  }, [wipePhase]);

  useEffect(() => {
    const timer = setInterval(() => {
      triggerSlide((slide + 1) % SLIDES.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slide, triggerSlide]);

  const current = SLIDES[displaySlide];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (forgotMode) {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { email });
        toast.success('Check your email for a reset link');
        setForgotMode(false);
      } else if (isLogin) {
        await login(email, password);
        toast.success('Welcome back!');
        navigate('/studio');
      } else {
        await register(email, password, name);
        toast.success('Account created successfully!');
        navigate('/studio');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">

      {/* ── Left carousel panel ── */}
      <div className="hidden lg:flex lg:w-[52%] bg-[#0a0a0a] text-white flex-col justify-between p-14 overflow-hidden relative">

        {/* Wipe overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: '#111111',
            borderRight: wipePhase !== 'idle'
              ? `1px solid ${(SLIDES[nextSlideRef.current] ?? current).accent}40`
              : 'none',
            zIndex: 20,
            pointerEvents: 'none',
            transformOrigin: wipePhase === 'out' ? 'right' : 'left',
            transform: wipePhase === 'in' ? 'scaleX(1)' : 'scaleX(0)',
            transition: wipePhase === 'idle'
              ? 'none'
              : 'transform 240ms cubic-bezier(0.76, 0, 0.24, 1)',
          }}
        />

        {/* Slide content */}
        <div className="flex-1 flex flex-col justify-center">

          {/* Tag */}
          <div className="flex items-center gap-2 mb-10">
            {current.icon === 'workflow'
              ? <Workflow className="h-3.5 w-3.5" style={{ color: current.accent }} />
              : <PenLine className="h-3.5 w-3.5" style={{ color: current.accent + '99' }} />
            }
            <span
              className="text-xs uppercase tracking-widest font-['IBM_Plex_Sans']"
              style={{ color: current.accent + '70' }}
            >
              {current.tag}
            </span>
          </div>

          {/* Headline */}
          <h1
            className="text-5xl font-bold leading-tight tracking-tight mb-6"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {current.headline.map((line, i) => (
              <span key={i}>{line}{i < current.headline.length - 1 && <br />}</span>
            ))}
          </h1>

          {/* Body */}
          <p className="text-white/50 text-base font-['IBM_Plex_Sans'] leading-relaxed max-w-sm mb-12">
            {current.body}
          </p>

          {/* Features */}
          <div className="space-y-6">
            {current.features.map((f) => (
              <div key={f.title} className="flex items-start gap-4">
                <div
                  className="w-2 h-2 rounded-full shrink-0 mt-1.5"
                  style={{ backgroundColor: current.accent + '50' }}
                />
                <div>
                  <p className="text-base text-white/75 font-['IBM_Plex_Sans'] font-medium">
                    {f.title}
                  </p>
                  <p className="text-sm text-white/35 font-['IBM_Plex_Sans'] mt-0.5 leading-relaxed">
                    {f.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Carousel controls */}
        <div className="flex items-center gap-4 pt-10">
          {SLIDES.map((s, i) => (
            <button
              key={i}
              onClick={() => triggerSlide(i)}
              className="flex items-center gap-2.5 transition-all duration-300 px-3 py-1.5 rounded-full border"
              style={{
                borderColor: i === slide ? '#ffffff40' : '#ffffff15',
                backgroundColor: i === slide ? '#ffffff15' : 'transparent',
              }}
            >
              <div
                className="rounded-full transition-all duration-300 shrink-0"
                style={{
                  width: '6px',
                  height: '6px',
                  backgroundColor: i === slide ? current.accent : '#ffffff30',
                }}
              />
              <span
                className="text-xs font-['IBM_Plex_Sans'] transition-colors duration-300"
                style={{ color: i === slide ? '#ffffffcc' : '#ffffff50' }}
              >
                {s.tag}
              </span>
            </button>
          ))}
        </div>

      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-sm">

          {/* Mobile-only header */}
          <div className="mb-10 text-center lg:hidden">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Workflow className="h-5 w-5 text-primary" />
              <h1 className="text-2xl font-bold tracking-tight">BA Workbench</h1>
            </div>
            <p className="text-sm text-muted-foreground font-['IBM_Plex_Sans']">
              Requirements · Compliance · Delivery
            </p>
          </div>

          <div className="bg-card border border-border p-8 rounded-lg shadow-sm">
            {!forgotMode && (
              <div className="flex gap-4 mb-8">
                <button
                  data-testid="tab-login"
                  onClick={() => setIsLogin(true)}
                  className={`flex-1 pb-2 font-medium tracking-wide uppercase text-sm transition-colors ${
                    isLogin ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'
                  }`}
                >
                  Login
                </button>
                <button
                  data-testid="tab-register"
                  onClick={() => setIsLogin(false)}
                  className={`flex-1 pb-2 font-medium tracking-wide uppercase text-sm transition-colors ${
                    !isLogin ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'
                  }`}
                >
                  Register
                </button>
              </div>
            )}

            {forgotMode && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold tracking-tight">Forgot Password</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter your email and we'll send you a reset link.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && !forgotMode && (
                <div>
                  <Label htmlFor="name" className="uppercase tracking-widest text-xs font-medium">
                    Name
                  </Label>
                  <Input
                    id="name"
                    data-testid="input-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="mt-2"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="email" className="uppercase tracking-widest text-xs font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  data-testid="input-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-2"
                />
              </div>

              {!forgotMode && (
                <div>
                  <Label htmlFor="password" className="uppercase tracking-widest text-xs font-medium">
                    Password
                  </Label>
                  <div className="relative mt-2">
                    <Input
                      id="password"
                      data-testid="input-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => setForgotMode(true)}
                      className="text-xs text-muted-foreground hover:text-foreground mt-2 transition-colors"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
              )}

              <Button
                data-testid="btn-submit"
                type="submit"
                disabled={loading}
                className="w-full uppercase tracking-wide font-medium"
              >
                {loading
                  ? 'Please wait…'
                  : forgotMode
                  ? 'Send Reset Link'
                  : isLogin
                  ? 'Sign In'
                  : 'Create Account'}
              </Button>

              {forgotMode && (
                <button
                  type="button"
                  onClick={() => setForgotMode(false)}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Back to login
                </button>
              )}
            </form>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6 font-['IBM_Plex_Sans']">
            Requirements · Compliance · Delivery · AI-assisted
          </p>
          <p className="text-center text-xs text-muted-foreground mt-3 font-['IBM_Plex_Sans']">
            Free to use.{' '}
            <a href="#/privacy" className="underline hover:text-foreground transition-colors">
              We never sell your data.
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
